/**
 * ConfigForm — the tenant detail panel's "Cadence & backfill" form
 * (WSRPDM-0012, contract §1.5). The 5 config knobs, exactly the contract's
 * field names: `userSyncFrequencyHours` (hours), `userSyncSummarySendTimes`
 * (comma-sep HH:mm), `backfillStartDate` (yyyy-MM-dd),
 * `backfillCooldownMinutes` (minutes), `dailySyncTime` (HH:mm).
 *
 * Local form state is seeded once from `config` via a lazy initializer — no
 * prop-sync effect. The parent remounts this component with `key={slug}`
 * when the open tenant changes, which resets local state for free; after a
 * successful save this component re-seeds itself directly from the PUT's
 * re-GET response (server-normalized values, e.g. a cleared field's applied
 * default).
 *
 * A 400 `INVALID_INGESTION_CONFIG` carries a structured `field` [WSRAPI-0034]
 * naming the offending JSON property — which matches these field names
 * verbatim — so the matching TextField is highlighted with the server's
 * `message`, no client-side re-derivation needed.
 */
import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { updateConfig } from '@/api/wsr/tenants';
import { wsrApiError } from '@/api/wsr/errors';
import type {
  IngestionConfigUpdateRequest,
  IngestionConfigView,
  PlatformTenantDetail,
} from '@/api/wsr/types';

interface FormState {
  userSyncFrequencyHours: string;
  userSyncSummarySendTimes: string;
  backfillStartDate: string;
  backfillCooldownMinutes: string;
  dailySyncTime: string;
}

function toFormState(config: IngestionConfigView): FormState {
  return {
    userSyncFrequencyHours: String(config.userSyncFrequencyHours),
    userSyncSummarySendTimes: config.userSyncSummarySendTimes ?? '',
    backfillStartDate: config.backfillStartDate,
    backfillCooldownMinutes: String(config.backfillCooldownMinutes),
    dailySyncTime: config.dailySyncTime,
  };
}

/** Blank ⇒ null, which the contract defines as "clear this knob to default". */
function toUpdateRequest(form: FormState): IngestionConfigUpdateRequest {
  const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());
  const numberOrNull = (v: string): number | null => (v.trim() === '' ? null : Number(v));
  return {
    userSyncFrequencyHours: numberOrNull(form.userSyncFrequencyHours),
    userSyncSummarySendTimes: orNull(form.userSyncSummarySendTimes),
    backfillStartDate: orNull(form.backfillStartDate),
    backfillCooldownMinutes: numberOrNull(form.backfillCooldownMinutes),
    dailySyncTime: orNull(form.dailySyncTime),
  };
}

interface ConfigFormProps {
  slug: string;
  config: IngestionConfigView;
  onSaved: (detail: PlatformTenantDetail) => void;
}

export function ConfigForm({ slug, config, onSaved }: ConfigFormProps) {
  const { t } = useCopy();
  const [form, setForm] = useState<FormState>(() => toFormState(config));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorField, setErrorField] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const setField = (field: keyof FormState) => (value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorField(null);
    setErrorMessage(null);
    setSaved(false);
    try {
      const detail = await updateConfig(slug, toUpdateRequest(form));
      setForm(toFormState(detail.config));
      setSaved(true);
      onSaved(detail);
    } catch (err) {
      const wsrErr = wsrApiError(err);
      if (wsrErr?.field) {
        setErrorField(wsrErr.field);
        setErrorMessage(wsrErr.message);
      } else {
        setErrorMessage(
          wsrErr?.message ??
            t('tenantDetail.config.saveError', 'Could not save configuration. Please try again.'),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (field: keyof FormState): string | undefined =>
    errorField === field ? (errorMessage ?? undefined) : undefined;

  return (
    <Box
      component="form"
      aria-label={t('tenantDetail.config.formAriaLabel', 'Tenant configuration form')}
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {t('tenantDetail.config.title', 'Configuration')}
      </Typography>

      {errorMessage && !errorField ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}
      {saved ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('tenantDetail.config.saveSuccess', 'Configuration saved.')}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <TextField
          label={t('tenantDetail.config.userSyncFrequency', 'User sync frequency (hours)')}
          helperText={
            fieldError('userSyncFrequencyHours') ??
            t(
              'tenantDetail.config.userSyncFrequencyHelper',
              'How often the WStudio→GHL user sync runs. Minimum 1.',
            )
          }
          error={errorField === 'userSyncFrequencyHours'}
          type="number"
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
          value={form.userSyncFrequencyHours}
          onChange={(e) => setField('userSyncFrequencyHours')(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label={t('tenantDetail.config.dailySyncTime', 'Daily sync time (HH:mm)')}
          helperText={
            fieldError('dailySyncTime') ??
            t(
              'tenantDetail.config.dailySyncTimeHelper',
              'When the daily previous-day pull runs, in app timezone.',
            )
          }
          error={errorField === 'dailySyncTime'}
          type="time"
          value={form.dailySyncTime}
          onChange={(e) => setField('dailySyncTime')(e.target.value)}
          size="small"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label={t('tenantDetail.config.userSyncSendTimes', 'User sync summary send times')}
          placeholder={t('tenantDetail.config.userSyncSendTimesPlaceholder', 'e.g. 09:00,17:00')}
          helperText={
            fieldError('userSyncSummarySendTimes') ??
            t(
              'tenantDetail.config.userSyncSendTimesHelper',
              'Comma-separated HH:mm times for the daily digest email (app timezone). Leave empty for no scheduled report.',
            )
          }
          error={errorField === 'userSyncSummarySendTimes'}
          value={form.userSyncSummarySendTimes}
          onChange={(e) => setField('userSyncSummarySendTimes')(e.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          label={t('tenantDetail.config.backfillStartDate', 'Backfill start date')}
          helperText={
            fieldError('backfillStartDate') ??
            t(
              'tenantDetail.config.backfillStartDateHelper',
              'Genesis date for sales/attendance backfill (YYYY-MM-DD).',
            )
          }
          error={errorField === 'backfillStartDate'}
          type="date"
          value={form.backfillStartDate}
          onChange={(e) => setField('backfillStartDate')(e.target.value)}
          size="small"
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label={t('tenantDetail.config.backfillCooldown', 'Backfill cooldown (minutes)')}
          helperText={
            fieldError('backfillCooldownMinutes') ??
            t(
              'tenantDetail.config.backfillCooldownHelper',
              'Rest between weekly backfill chunks in minutes.',
            )
          }
          error={errorField === 'backfillCooldownMinutes'}
          type="number"
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
          value={form.backfillCooldownMinutes}
          onChange={(e) => setField('backfillCooldownMinutes')(e.target.value)}
          size="small"
          fullWidth
        />
        <Box>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving
              ? t('tenantDetail.config.saving', 'Saving…')
              : t('tenantDetail.config.saveButton', 'Save configuration')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
